import DOMPurify from 'dompurify';

/**
 * 공지 본문(HTML) sanitize.
 * 원본: naegift-user-app/src/utils/sanitizeNoticeHtml.ts (타입 표기만 제거)
 *
 * [html-input] 본문 형식(content_format)에 따라 규칙이 둘이다.
 *  · quill : 운영자가 Quill 에디터로 작성한 HTML (naegift-operator NoticeManage.jsx QUILL_FORMATS).
 *            화이트리스트는 그 formats 목록 = header(h1~h3)·bold·italic·underline·color·list·link 의
 *            quill 1.3.7 출력 태그와 일치시킨다. 그 밖의 태그(table/script/iframe 등)는 전부 제거된다.
 *  · html  : 다른 곳에서 만든 HTML 을 붙여 넣은 본문. 구조·표 태그와 인라인 style 을 남기되
 *            style 은 허용 목록 + 값 검사로 좁힌다(DOMPurify 는 style 값을 검사하지 않는다).
 *            <style> 태그와 ql-* 밖의 class 는 지운다.
 * [notice-002] img 는 https:// 주소만 남긴다(호스트 무관). http:·data:(base64)·상대경로 img 는 제거된다.
 *
 * ★DB 에 직접 들어간 본문까지 신뢰하지 않는다 — 렌더 직전에 항상 이 함수를 거친다.
 * ★SYNC: 같은 코드가 3곳에 있다 — naegift-user-app(src/utils/sanitizeNoticeHtml.ts),
 *   naegift-admin-app(src/utils/sanitizeNoticeHtml.js), naegift-operator(src/utils/sanitizeNoticeHtml.js).
 *   고칠 때 세 곳과 NOTICE_SANITIZE_VERSION 을 함께 바꾸고, 같은 테스트 케이스를 맞춘다.
 */
export const NOTICE_SANITIZE_VERSION = 'html-input-4';

const ALLOWED_TAGS = [
  'p',
  'br',
  'span',
  'strong',
  'em',
  'u',
  's',
  'h1',
  'h2',
  'h3',
  'ol',
  'ul',
  'li',
  'a',
  'blockquote',
  'img',
];

/**
 * DOMPurify 의 ALLOWED_ATTR 은 태그별 지정이 안 된다 — 여기서 크게 열고
 * afterSanitizeAttributes 훅에서 태그별로 좁힌다(style 은 span 의 color 만, class 는 ql-* 만).
 */
const ALLOWED_ATTR = ['href', 'target', 'rel', 'style', 'class', 'src', 'alt'];

/** [html-input] HTML 모드 태그. 기본 목록(ADD_TAGS)은 form·style·video 등을 포함하므로 쓰지 않는다. */
const HTML_ALLOWED_TAGS = [
  ...ALLOWED_TAGS,
  'div',
  'section',
  'article',
  'header',
  'footer',
  'aside',
  'figure',
  'figcaption',
  'hr',
  'center',
  'h4',
  'h5',
  'h6',
  'b',
  'i',
  'small',
  'sub',
  'sup',
  'mark',
  'del',
  'ins',
  'strike',
  'code',
  'pre',
  'dl',
  'dt',
  'dd',
  'table',
  'caption',
  'colgroup',
  'col',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
];

const HTML_ALLOWED_ATTR = [
  ...ALLOWED_ATTR,
  'title',
  'width',
  'height',
  'align',
  'valign',
  'bgcolor',
  'border',
  'cellpadding',
  'cellspacing',
  'colspan',
  'rowspan',
];

/** 허용 목록 실수에 대비한 안전망. FORBID_* 는 ALLOWED_* 보다 항상 우선한다. */
const HTML_FORBID_TAGS = [
  'style',
  'link',
  'meta',
  'base',
  'script',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'video',
  'audio',
  'svg',
  'math',
  'template',
];

/** quill 이 들여쓰기/정렬에 쓰는 클래스만 허용한다(ql-indent-1, ql-align-center …). */
const QUILL_CLASS_RE = /^ql-(?:indent|align)-[a-z0-9-]+$/i;

/** [html-input] 정화기가 직접 붙이는 표 가로 스크롤 틀. 입력에 들어 있는 같은 class 는 위 규칙으로 지워진다. */
const SCROLL_WRAPPER_CLASS = 'ql-notice-scroll';

const TABLE_TAGS = [
  'TABLE',
  'CAPTION',
  'COLGROUP',
  'COL',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'TH',
  'TD',
];

/** [html-input] 표 계열 옛 속성 값 규칙. 맞지 않으면 속성을 지운다. */
const TABLE_ATTR_RULES = {
  align: /^(left|center|right|justify)$/i,
  valign: /^(top|middle|bottom|baseline)$/i,
  bgcolor: /^#?[0-9a-z]{1,20}$/i,
  border: /^\d{1,2}$/,
  cellpadding: /^\d{1,2}$/,
  cellspacing: /^\d{1,2}$/,
  colspan: /^\d{1,2}$/,
  rowspan: /^\d{1,2}$/,
};

const SIZE_ATTR_RE = /^\d{1,4}%?$/;

const BLOCK_ALIGN_RE = /^(left|center|right|justify)$/i;

/**
 * [html-input] 남길 style 속성. 허용 목록 방식(차단 목록은 새 CSS 속성에 뚫린다).
 * 빠진 것과 이유:
 *  · position·top/left·z-index·transform·float : 본문 밖으로 나가 화면·버튼을 덮는다
 *  · min-width·min-height                        : 고정 최소 폭으로 가로 넘침
 *  · background(-image)·list-style-image 등      : 외부 URL 요청(추적)
 *  · font-family                                 : 앱 글꼴을 따른다(기존 디자인)
 */
const STYLE_PROPS = new Set([
  'color',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-decoration',
  'text-decoration-line',
  'text-decoration-color',
  'text-decoration-style',
  'text-decoration-thickness',
  'text-transform',
  'text-indent',
  'vertical-align',
  'white-space',
  'word-break',
  'overflow-wrap',
  'word-wrap',
  'width',
  'max-width',
  'height',
  'box-sizing',
  'background-color',
  'border-radius',
  'border-collapse',
  'border-spacing',
  'list-style-type',
  'list-style-position',
  'display',
  'opacity',
  'box-shadow',
]);

const STYLE_PROP_RES = [
  /^margin(-(top|right|bottom|left))?$/,
  /^padding(-(top|right|bottom|left))?$/,
  /^border(-(top|right|bottom|left))?(-(width|style|color))?$/,
  /^border-(width|style|color)$/,
  /^border-(top|bottom)-(left|right)-radius$/,
];

/** 값 어디에 있어도 지우는 것: 외부 요청·스크립트·CSS 이스케이프 우회·변수. */
const STYLE_VALUE_DENY_RE =
  /url\s*\(|image-set\s*\(|expression\s*\(|javascript:|@|\\|var\s*\(|env\s*\(|[<>]/i;

const DISPLAY_VALUES = new Set([
  'block',
  'inline',
  'inline-block',
  'table',
  'table-row',
  'table-cell',
  'none',
]);

const HEIGHT_RE = /^(auto|\d{1,3}(\.\d+)?(px|%|em|rem))$/;

// 음수 margin: -10px, -.5em, calc(…) 모두. margin 값에 '-' 숫자나 calc 가 올 정상 용도는 없다.
const NEGATIVE_RE = /-\s*[\d.]|calc\s*\(/i;

/**
 * 브라우저는 쇼트핸드(border·background·font)를 롱핸드로 펼치며 작성자가 쓰지 않은 칸을 기본값으로 채운다.
 * 그런 칸은 지워도 화면이 같으므로 집계(안내)에서 뺀다.
 *  · background·list-style·text-decoration 은 'initial' 로 채운다 → 아래 정규식
 *  · border 는 border-image-* 를, font 는 font-kerning 등을 실제 기본값(100%·stretch·auto…)으로 채운다
 *    (Chrome 155 확인) → 같은 쇼트핸드를 최소 값으로 넣은 비교용 요소의 값과 같으면 기본값으로 본다
 */
const STYLE_DEFAULT_VALUE_RE = /^(initial|inherit|unset|revert|none|normal)$/i;
const SHORTHAND_PROBE_VALUES = {
  border: 'none',
  'border-top': 'none',
  'border-right': 'none',
  'border-bottom': 'none',
  'border-left': 'none',
  background: 'none',
  'list-style': 'none',
  'text-decoration': 'none',
  // 글꼴 이름은 작성자 값과 겹치지 않게 — font-family 는 실제로 지워지므로 집계에 남아야 한다.
  font: '16px x-notice-probe',
};

/** DOMPurify.removed 집계에서 뺄 문서 틀. 화면 결과가 달라지지 않는데 매번 잡혀 안내를 흐린다. */
const REPORT_IGNORED_ELEMENTS = new Set([
  'remove',
  'meta',
  'title',
  'link',
  'base',
  '#comment',
  'html',
  'head',
  'body',
]);

/** 이번 sanitize 호출의 집계 대상. sanitize 는 동기라 호출 사이에 섞이지 않는다. */
let activeReport = null;

function count(map, key) {
  map[key] = (map[key] || 0) + 1;
}

function isStylePropAllowed(prop) {
  return STYLE_PROPS.has(prop) || STYLE_PROP_RES.some(re => re.test(prop));
}

function isStyleValueAllowed(prop, value) {
  const v = value.trim().toLowerCase();
  if (!v || STYLE_VALUE_DENY_RE.test(v)) return false;
  if (prop === 'display') return DISPLAY_VALUES.has(v);
  if (prop === 'height') return HEIGHT_RE.test(v);
  if (prop === 'opacity') {
    const n = parseFloat(v);
    return !Number.isNaN(n) && n >= 0.3;
  }
  if (prop.startsWith('margin')) return !NEGATIVE_RE.test(v);
  return true;
}

/**
 * 태그별 속성 좁히기 훅.
 *  · style   : span 의 color 만 남긴다(배경·폰트크기 등으로 레이아웃을 깨지 못하게).
 *  · class   : ql-indent-*, ql-align-* 만 남긴다(앱 CSS 클래스 사칭 방지).
 *  · a       : target=_blank + rel=noopener noreferrer 강제(탭 탈취 차단).
 *              href 스킴 검증(javascript:, data: 차단)은 DOMPurify 기본
 *              ALLOWED_URI_REGEXP 가 이미 처리하므로 여기서 다시 하지 않는다.
 */
function narrowAttributes(node) {
  if (typeof node?.getAttribute !== 'function') return;
  if (node.hasAttribute('style')) {
    const styled = node;
    const color = node.tagName === 'SPAN' ? styled.style?.color || '' : '';
    node.removeAttribute('style');
    if (color) {
      styled.style.color = color;
    }
  }
  narrowClass(node);
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

function narrowClass(node) {
  if (!node.hasAttribute('class')) return;
  const kept = Array.prototype.slice
    .call(node.classList)
    .filter(cls => QUILL_CLASS_RE.test(cls));
  if (kept.length) {
    node.setAttribute('class', kept.join(' '));
  } else {
    node.removeAttribute('class');
    if (activeReport) count(activeReport.attributes, 'class');
  }
}

/** [html-input] style 을 허용 속성·값만 남겨 다시 쓴다. !important 는 버린다(앱 CSS 의 넘침 방지를 이기지 못하게). */
/** style 속성 원문에 작성자가 쓴 속성 이름. 쇼트핸드는 쓴 이름 그대로(border) 들어간다. */
function declaredStyleNames(text) {
  const names = new Set();
  text.split(';').forEach(decl => {
    const colon = decl.indexOf(':');
    if (colon > 0) names.add(decl.slice(0, colon).trim().toLowerCase());
  });
  return names;
}

/**
 * [html-input] 지운 롱핸드가 작성자가 쓰지 않은, 쇼트핸드가 채운 기본값인지.
 * 작성자가 그 이름을 직접 썼으면 기본값이 아니다(집계). 쓴 쇼트핸드를 비교용 요소에 최소 값으로 넣어
 * 같은 값이 나오면 브라우저가 채운 기본값이다. background:url(…) 의 background-image 처럼 작성자 값이 든
 * 칸은 비교값과 달라 집계된다. (jsdom 은 쇼트핸드를 브라우저처럼 펼치지 않아 테스트에서 직접 부른다)
 */
export function isShorthandDefault(prop, value, declared, doc) {
  if (STYLE_DEFAULT_VALUE_RE.test(value.trim())) return true;
  if (declared.has(prop)) return false;
  return Array.from(declared).some(name => {
    const minimal = SHORTHAND_PROBE_VALUES[name];
    if (!minimal) return false;
    const probe = doc.createElement('div');
    probe.style.setProperty(name, minimal);
    return probe.style.getPropertyValue(prop) === value;
  });
}

function narrowStyle(node) {
  if (!node.hasAttribute('style')) return;
  const declared = declaredStyleNames(node.getAttribute('style') || '');
  const style = node.style;
  const kept = [];
  for (let i = 0; i < style.length; i += 1) {
    const prop = style.item(i);
    const value = style.getPropertyValue(prop);
    if (isStylePropAllowed(prop) && isStyleValueAllowed(prop, value)) {
      kept.push([prop, value]);
    } else if (
      activeReport &&
      !isShorthandDefault(prop, value, declared, node.ownerDocument)
    ) {
      count(activeReport.styles, prop);
    }
  }
  node.removeAttribute('style');
  kept.forEach(([prop, value]) => node.style.setProperty(prop, value));
  if (!node.getAttribute('style')) node.removeAttribute('style');
}

function dropAttr(node, name) {
  node.removeAttribute(name);
  if (activeReport) count(activeReport.attributes, name);
}

/** [html-input] 이메일 열람 추적용 1×1 이미지인지(속성 또는 style 폭·높이가 1 이하). */
function isTrackingImage(node) {
  const tiny = v => v != null && /^[01](px)?$/.test(v.trim());
  return (
    (tiny(node.getAttribute('width')) && tiny(node.getAttribute('height'))) ||
    (tiny(node.style.width) && tiny(node.style.height))
  );
}

/** width 속성 또는 인라인 width 를 읽어 지운다. 속성 숫자는 px 이다. */
function takeWidth(el) {
  const attr = el.getAttribute('width');
  el.removeAttribute('width');
  const inline = el.style.width;
  el.style.removeProperty('width');
  if (inline) return inline.trim();
  if (attr) return /%$/.test(attr) ? attr : `${attr}px`;
  return '';
}

const PX_RE = /^(\d+(?:\.\d+)?)px$/;

/**
 * [html-input] 표 고정 폭 600px → width:100% + max-width:600px. 화면이 넓으면 원래 폭, 좁으면 화면 폭.
 * ★Chrome 은 표의 max-width 만으로는 고정 width 를 줄이지 않는다(휴대폰 폭 실측) — width 자체를 바꿔야 한다.
 */
function fitTableWidth(el) {
  const width = takeWidth(el);
  if (!width) return;
  const px = PX_RE.exec(width);
  if (px) {
    el.style.width = '100%';
    el.style.maxWidth = `${px[1]}px`;
  } else {
    el.style.width = width;
  }
}

/**
 * [html-input] 칸 px 폭 → 바깥 표 원래 폭에 대한 비율(%). 자동 표 배치에서 칸의 px 폭은 최소 폭이라
 * 그대로 두면 표가 줄지 못한다(CSS 2.1 17.5.2.2). 바깥 표 폭을 모르면 px 폭은 지운다.
 * 훅은 문서 순서로 돌아 바깥 표가 먼저 처리되므로 그 max-width 에 원래 px 이 들어 있다.
 */
function fitCellWidth(el) {
  const width = takeWidth(el);
  if (!width) return;
  if (/%$/.test(width)) {
    el.style.width = width;
    return;
  }
  const px = PX_RE.exec(width);
  const table = el.parentElement?.closest('table');
  const tablePx = table ? PX_RE.exec(table.style.maxWidth || '') : null;
  if (px && tablePx) {
    const pct = Math.min(
      100,
      (parseFloat(px[1]) / parseFloat(tablePx[1])) * 100
    );
    el.style.width = `${Math.round(pct * 100) / 100}%`;
  }
}

/**
 * [html-input] 표 속성(border·cellpadding)은 그 표의 칸에도 적용된다. 훅은 문서 순서로 돌아 표가 칸보다
 * 먼저 처리되므로, 표를 처리할 때 칸에 줄 값을 여기 적어 둔다(이번 sanitize 호출 동안만 쓴다).
 */
let tableCellFormat = new WeakMap();

const CELL_TAGS = ['TD', 'TH'];
const ROW_TAGS = ['TR', 'THEAD', 'TBODY', 'TFOOT'];

/** 원문 style 에 같은 속성(또는 그 롱핸드)이 이미 있으면 원문을 남긴다. */
function setStyleIfEmpty(el, prop, value) {
  const style = el.style;
  for (let i = 0; i < style.length; i += 1) {
    const name = style.item(i);
    if (name === prop || name.indexOf(`${prop}-`) === 0) return;
  }
  style.setProperty(prop, value);
}

/** 속성을 읽어 지운다(옮기는 것이라 "지운 부분" 집계에 넣지 않는다). */
function takeAttr(el, name) {
  const value = el.getAttribute(name);
  el.removeAttribute(name);
  return value;
}

/**
 * [html-input] 표현용 HTML 속성 → 인라인 style. 표현 속성은 앱 CSS 규칙에 지므로(admin-app 전역 리셋이
 * td padding·border·background 를 0·투명으로, table 을 collapse 로 덮는다) 인라인으로 옮겨 두 앱을 같게 한다.
 * 값은 브라우저 기본 표현(Chrome 실측)과 같다: 표 border 는 outset, 그 칸은 1px inset, 칸 여백 기본 1px,
 * 칸 간격 기본 separate 2px, 색은 글자색.
 */
function movePresentationalAttrs(el) {
  const tag = el.tagName;
  if (tag === 'TABLE') {
    const border = parseInt(takeAttr(el, 'border') || '0', 10);
    const padding = takeAttr(el, 'cellpadding');
    const spacing = takeAttr(el, 'cellspacing');
    const align = (takeAttr(el, 'align') || '').toLowerCase();
    const bgcolor = takeAttr(el, 'bgcolor');
    if (border > 0) setStyleIfEmpty(el, 'border', `${border}px outset`);
    setStyleIfEmpty(el, 'border-collapse', 'separate');
    setStyleIfEmpty(el, 'border-spacing', `${spacing || 2}px`);
    // align=center 는 가운데 배치. left·right 는 표를 띄우는(float) 뜻이라 옮기지 않고 지운다.
    if (align === 'center') {
      setStyleIfEmpty(el, 'margin-left', 'auto');
      setStyleIfEmpty(el, 'margin-right', 'auto');
    }
    if (bgcolor) setStyleIfEmpty(el, 'background-color', bgcolor);
    tableCellFormat.set(el, {
      border: border > 0,
      padding: `${padding || 1}px`,
    });
    return;
  }
  const isCell = CELL_TAGS.indexOf(tag) !== -1;
  if (isCell || ROW_TAGS.indexOf(tag) !== -1) {
    if (isCell) {
      const table = el.parentElement?.closest('table');
      const format = table ? tableCellFormat.get(table) : undefined;
      if (format?.border) setStyleIfEmpty(el, 'border', '1px inset');
      setStyleIfEmpty(el, 'padding', format ? format.padding : '1px');
    }
    const bgcolor = takeAttr(el, 'bgcolor');
    const align = takeAttr(el, 'align');
    const valign = takeAttr(el, 'valign');
    if (bgcolor) setStyleIfEmpty(el, 'background-color', bgcolor);
    if (align) setStyleIfEmpty(el, 'text-align', align.toLowerCase());
    if (valign) setStyleIfEmpty(el, 'vertical-align', valign.toLowerCase());
    return;
  }
  if (!el.hasAttribute('align')) return;
  const align = (takeAttr(el, 'align') || '').toLowerCase();
  // img·caption·col 의 align 은 띄우기·위치 뜻이라 옮기지 않는다. 그 밖(div·p·제목 등)은 글 정렬.
  if (['IMG', 'CAPTION', 'COL', 'COLGROUP'].indexOf(tag) === -1) {
    setStyleIfEmpty(el, 'text-align', align);
  }
}

/** [html-input] HTML 모드 태그별 속성 좁히기 훅. */
function narrowHtmlAttributes(node) {
  if (typeof node?.getAttribute !== 'function') return;
  const el = node;
  const tag = node.tagName;
  // 결과는 body 의 안쪽만 내보내므로 body 자체 속성은 화면에 닿지 않는다(집계는 bodyAttributes 가 따로 함).
  if (tag === 'BODY') return;
  const isTable = TABLE_TAGS.indexOf(tag) !== -1;
  if (activeReport && tag === 'IMG' && isTrackingImage(el)) {
    activeReport.trackingImages += 1;
  }
  narrowStyle(el);
  narrowClass(node);
  ['width', 'height'].forEach(name => {
    if (!node.hasAttribute(name)) return;
    const value = node.getAttribute(name) || '';
    if ((!isTable && tag !== 'IMG') || !SIZE_ATTR_RE.test(value)) {
      dropAttr(node, name);
    }
  });
  // 고정 폭을 화면에 맞춰 줄어드는 폭으로 바꾼다(휴대폰에서 600px 이메일 표가 통째로 가로 스크롤되지 않게).
  // 인라인으로 두면 admin-app 전역 table{width:100%} 보다도 우선해 두 앱 표시가 같다.
  if (tag === 'TABLE') {
    fitTableWidth(el);
  } else if (tag === 'TD' || tag === 'TH' || tag === 'COL') {
    fitCellWidth(el);
  } else if (tag === 'IMG') {
    el.style.maxWidth = '100%';
    el.style.height = 'auto';
  }
  Object.keys(TABLE_ATTR_RULES).forEach(name => {
    if (!node.hasAttribute(name)) return;
    const value = node.getAttribute(name) || '';
    const allowedHere =
      isTable || (name === 'align' && BLOCK_ALIGN_RE.test(value));
    if (!allowedHere || !TABLE_ATTR_RULES[name].test(value)) {
      dropAttr(node, name);
    }
  });
  movePresentationalAttrs(el);
  if (tag === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

/** [notice-002] 절대 주소이면서 https: 인지. 상대경로·//host·data:·http: 는 false. */
function isHttpsUrl(value) {
  try {
    return new URL(value.trim()).protocol === 'https:';
  } catch (e) {
    return false;
  }
}

/**
 * [notice-002] img 의 src 는 https 만 남긴다.
 * ★DOMPurify 기본 URI 규칙은 img 에 data: 를 허용하고 상대경로·//host 도 통과시키므로
 *   ALLOWED_ATTR 에 src 를 여는 것만으로는 막히지 않는다. ALLOWED_URI_REGEXP 를 바꾸면 a 의 href 규칙까지
 *   바뀌므로 img 에만 거는 훅을 쓴다.
 */
function keepHttpsImageSrc(node, data) {
  if (node.tagName !== 'IMG' || data.attrName !== 'src') return;
  if (!isHttpsUrl(data.attrValue || '')) {
    data.keepAttr = false;
  }
}

/**
 * [html-input] HTML 모드 본문 링크는 https·mailto·tel 만. 상대경로(/notice)는 user-app·admin-app 에서
 *  다른 화면을 가리키므로 받지 않는다(버튼 링크를 전체 주소만 받는 것과 같은 이유).
 */
function keepAbsoluteHref(node, data) {
  if (node.tagName !== 'A' || data.attrName !== 'href') return;
  const value = (data.attrValue || '').trim();
  const ok = /^(mailto|tel):/i.test(value) || isHttpsUrl(value);
  if (!ok) {
    data.keepAttr = false;
  }
}

/** [notice-002] src 가 남지 않은 img 는 노드째 지운다(빈 이미지 틀·alt 글자만 남지 않게). */
function removeImageWithoutSrc(node) {
  if (node.tagName === 'IMG' && !node.hasAttribute('src')) {
    node.parentNode?.removeChild(node);
    if (activeReport) activeReport.images += 1;
  }
}

/** [html-input] 바깥쪽 표만 가로 스크롤 틀로 감싼다(안쪽 표는 바깥 틀 안에서 함께 스크롤). */
function wrapOuterTables(root) {
  const tables = Array.prototype.slice.call(root.querySelectorAll('table'));
  tables.forEach(table => {
    if (table.parentElement?.closest('table')) return;
    const wrapper = table.ownerDocument.createElement('div');
    wrapper.className = SCROLL_WRAPPER_CLASS;
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
}

function collectRemoved(report) {
  (DOMPurify.removed || []).forEach(item => {
    if (item.element) {
      const name = String(item.element.nodeName || '').toLowerCase();
      if (!REPORT_IGNORED_ELEMENTS.has(name)) count(report.tags, name);
    } else if (item.attribute) {
      const from = String(item.from?.nodeName || '').toLowerCase();
      if (REPORT_IGNORED_ELEMENTS.has(from)) return;
      count(report.attributes, String(item.attribute.name || '').toLowerCase());
    }
  });
}

/** operator 미리보기용 "지워진 부분" 집계 객체. 안내용일 뿐 보안 판단에 쓰지 않는다(DOMPurify README). */
function emptyReport() {
  return {
    tags: {},
    attributes: {},
    styles: {},
    images: 0,
    trackingImages: 0,
    bodyAttributes: false,
  };
}

function sanitizeQuill(html) {
  DOMPurify.addHook('uponSanitizeAttribute', keepHttpsImageSrc);
  DOMPurify.addHook('afterSanitizeAttributes', narrowAttributes);
  DOMPurify.addHook('afterSanitizeAttributes', removeImageWithoutSrc);
  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', removeImageWithoutSrc);
    DOMPurify.removeHook('afterSanitizeAttributes', narrowAttributes);
    DOMPurify.removeHook('uponSanitizeAttribute', keepHttpsImageSrc);
  }
}

function sanitizeHtml(html, report) {
  activeReport = report || null;
  tableCellFormat = new WeakMap();
  if (report) {
    // <body>·<html> 의 속성은 파서가 버려 DOMPurify.removed 에도 남지 않는다.
    report.bodyAttributes =
      /<(body|html)\b[^>]*\s(style|bgcolor|class|background)\s*=/i.test(html);
  }
  DOMPurify.addHook('uponSanitizeAttribute', keepHttpsImageSrc);
  DOMPurify.addHook('uponSanitizeAttribute', keepAbsoluteHref);
  DOMPurify.addHook('afterSanitizeAttributes', narrowHtmlAttributes);
  DOMPurify.addHook('afterSanitizeAttributes', removeImageWithoutSrc);
  try {
    // FORCE_BODY: 앞쪽 <style> 이 <head> 로 가 기록 없이 사라지지 않게 body 로 붙든다(집계용).
    const body = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: HTML_ALLOWED_TAGS,
      ALLOWED_ATTR: HTML_ALLOWED_ATTR,
      FORBID_TAGS: HTML_FORBID_TAGS,
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
      FORCE_BODY: true,
      RETURN_DOM: true,
    });
    if (report) collectRemoved(report);
    wrapOuterTables(body);
    return body.innerHTML;
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', removeImageWithoutSrc);
    DOMPurify.removeHook('afterSanitizeAttributes', narrowHtmlAttributes);
    DOMPurify.removeHook('uponSanitizeAttribute', keepAbsoluteHref);
    DOMPurify.removeHook('uponSanitizeAttribute', keepHttpsImageSrc);
    activeReport = null;
  }
}

/**
 * 훅은 DOMPurify 인스턴스 전역이라 등록해두면 앱의 다른 sanitize 까지 영향받는다.
 * 호출마다 add → sanitize → remove 로 감싸 이 함수 밖으로 새지 않게 한다
 * (sanitize 는 동기라 이 구간에 다른 호출이 끼어들지 않는다).
 * format 이 'html' 이 아니면(없음·구버전 API 포함) quill 규칙이다.
 */
export function sanitizeNoticeHtml(html, format, report) {
  if (!html) return '';
  try {
    return format === 'html' ? sanitizeHtml(html, report) : sanitizeQuill(html);
  } catch (e) {
    // sanitize 실패 시 원문을 그대로 내보내지 않는다 — 빈 본문이 낫다.
    console.error(e);
    return '';
  }
}

export { emptyReport as createNoticeSanitizeReport };

export default sanitizeNoticeHtml;
